import { ArrowRight, Check, Menu, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import "@fontsource/instrument-serif/400.css";
import cataloguePhoto from "./assets/listwork-catalogue-1200.webp";
import heroPhoto from "./assets/listwork-hero-1600.webp";
import heroPhotoMobile from "./assets/listwork-hero-640.webp";
import heroPhotoTablet from "./assets/listwork-hero-960.webp";
import { ListworkMark } from "./components/brand/ListworkMark";
import "./marketing.css";

export type MarketingRoute = "home" | "pricing" | "privacy" | "terms" | "cookies";

const createAccountURL = "/login?mode=create";
const signInURL = "/login?mode=signin";
const marketingPublic = import.meta.env.VITE_REOS_MARKETING_PUBLIC !== "false";
const privateBetaSignInURL = import.meta.env.VITE_REOS_PRIVATE_BETA_SIGN_IN_URL || signInURL;

const plans = [
  {
    name: "Starter",
    price: "£12",
    description: "For a specialist solo seller building a disciplined catalogue.",
    features: ["250 inventory items", "Marketplace-ready drafts for five channels", "Guided posting and status tracking", "5 GB photo storage", "One seller seat"],
    featured: false
  },
  {
    name: "Pro",
    price: "£24",
    description: "For an established specialist business with more stock and support.",
    features: ["2,500 inventory items", "Everything in Starter", "50 GB photo storage", "Three team seats", "Full sales and profit history"],
    featured: true
  },
  {
    name: "Team",
    price: "£49",
    description: "For specialist teams running one shared catalogue and process.",
    features: ["10,000 inventory items", "Everything in Pro", "200 GB photo storage", "Ten team seats", "Shared operational workspace"],
    featured: false
  }
] as const;

const legalCompanyName = "N-Dimensional Limited t/a Listwork";
const legalAddress = "13 Round Close Road, Adderbury, Banbury, England, OX17 3EE";

export function MarketingSite({ route }: { route: MarketingRoute }) {
  useDocumentMetadata(route);
  const privateBetaMarketingRoute = !marketingPublic && (route === "home" || route === "pricing");

  useEffect(() => {
    if (privateBetaMarketingRoute) {
      window.location.replace(privateBetaSignInURL);
    }
  }, [privateBetaMarketingRoute]);

  if (privateBetaMarketingRoute) {
    return null;
  }

  if (route === "privacy") {
    return <LegalPage title="Privacy notice" summary="How Listwork collects, uses and protects personal information."><PrivacyContent /></LegalPage>;
  }
  if (route === "terms") {
    return <LegalPage title="Terms of service" summary="The terms that apply when you create or use a Listwork account."><TermsContent /></LegalPage>;
  }
  if (route === "cookies") {
    return <LegalPage title="Cookie notice" summary="The essential browser storage Listwork uses to keep accounts secure and the product working."><CookiesContent /></LegalPage>;
  }

  return route === "pricing" ? <PricingPage /> : <HomePage />;
}

function HomePage() {
  return (
    <div className="marketing-site">
      <MarketingHeader />
      <main id="main-content">
        <Hero />
        <MethodSection />
        <PositioningSection />
        <EvidenceSection />
        <AudienceSection />
        <CapabilitySection />
        <PricingSection compact />
        <FaqSection />
        <FinalCallToAction />
      </main>
      <MarketingFooter />
    </div>
  );
}

function PricingPage() {
  return (
    <div className="marketing-site">
      <MarketingHeader />
      <main id="main-content">
        <section className="marketing-page-intro pricing-intro">
          <div className="marketing-container">
            <p className="marketing-eyebrow">Simple, capacity-based pricing</p>
            <h1>Professional tools without a cut of your sales.</h1>
            <p>Start with every core workflow for 14 days. No card required, no setup fee, and no marketplace commission.</p>
          </div>
        </section>
        <PricingSection />
        <FaqSection />
        <FinalCallToAction />
      </main>
      <MarketingFooter />
    </div>
  );
}

function MarketingHeader() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="marketing-header">
      <a className="marketing-skip-link" href="#main-content">Skip to content</a>
      <div className="marketing-container marketing-nav">
        <BrandLink />
        <nav className={menuOpen ? "marketing-nav-links open" : "marketing-nav-links"} aria-label="Main navigation">
          <a href="/#method" onClick={() => setMenuOpen(false)}>Method</a>
          <a href="/#product" onClick={() => setMenuOpen(false)}>Product</a>
          <a href="/#specialists" onClick={() => setMenuOpen(false)}>For specialists</a>
          <a href="/pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
          <a className="mobile-sign-in" href={signInURL}>Sign in</a>
          <a className="mobile-start-trial" href={createAccountURL}>Start trial</a>
        </nav>
        <div className="marketing-nav-actions">
          <a className="marketing-text-link" href={signInURL}>Sign in</a>
          <a className="marketing-button nav-cta" href={createAccountURL}>Start trial <ArrowRight size={16} /></a>
          <button
            className="marketing-menu-button"
            type="button"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((current) => !current)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
    </header>
  );
}

function BrandLink() {
  return (
    <a className="listwork-brand" href="/" aria-label="Listwork home">
      <ListworkMark className="listwork-mark" />
      <span>listwork</span>
    </a>
  );
}

function Hero() {
  return (
    <section className="marketing-hero">
      <picture className="hero-picture">
        <source media="(max-width: 680px)" srcSet={heroPhotoMobile} />
        <source media="(max-width: 1100px)" srcSet={heroPhotoTablet} />
        <img src={heroPhoto} alt="A navy workwear jacket, vintage camera, watch, lamp and ceramic vessel arranged for resale research" />
      </picture>
      <div className="marketing-container hero-layout">
        <div className="hero-content">
          <p className="marketing-eyebrow">Research and listing tools for specialist resellers</p>
          <h1><span>Know what you have.</span><span>Show what it’s worth.</span></h1>
          <p className="hero-detail">Listwork helps you identify, research and position distinctive inventory, then turn the evidence into stronger marketplace-ready listings.</p>
          <div className="hero-actions">
            <a className="marketing-button" href={createAccountURL} data-conversion="hero-start-trial">Start 14-day trial <ArrowRight size={18} /></a>
            <a className="hero-secondary-link" href="#method">See the method <ArrowRight size={16} /></a>
          </div>
          <div className="hero-assurance" aria-label="Trial details">
            <span><Check size={16} /> No card required</span>
            <span><Check size={16} /> From £12/month</span>
            <span><Check size={16} /> No sales commission</span>
          </div>
        </div>
        <article className="hero-evidence" aria-label="Illustrative comparable-sales analysis">
          <p>Illustrative analysis</p>
          <h2>£128–£156</h2>
          <dl>
            <div><dt>Object</dt><dd>Navy chore jacket</dd></div>
            <div><dt>Evidence</dt><dd>3 comparable sales</dd></div>
            <div><dt>Condition</dt><dd>Very good</dd></div>
          </dl>
        </article>
      </div>
    </section>
  );
}

const methodSteps = [
  ["01", "Capture", "Photograph the item and keep its first clues, condition and stock details together."],
  ["02", "Identify", "Use assistive AI to surface likely maker, model, era and the details worth checking."],
  ["03", "Research", "Compare relevant market evidence and understand what changes the item’s value."],
  ["04", "Refine", "Apply your judgment to the title, condition, photographs, price and positioning."],
  ["05", "List", "Adapt the finished record for each marketplace and keep every result connected."]
] as const;

function MethodSection() {
  return (
    <section className="method-section" id="method">
      <div className="marketing-container method-heading">
        <div><p className="marketing-eyebrow on-dark">The Listwork method</p><h2>From first clue<br />to confident listing.</h2></div>
        <p>One continuous record for the work that determines what an item is, how it should be valued and how buyers should see it.</p>
      </div>
      <ol className="marketing-container method-steps">
        {methodSteps.map(([number, title, copy]) => <li key={title}><span>{number}</span><h3>{title}</h3><p>{copy}</p></li>)}
      </ol>
    </section>
  );
}

function PositioningSection() {
  return (
    <section className="positioning-section">
      <div className="positioning-copy">
        <p className="marketing-eyebrow">Why Listwork</p>
        <h2>The work before the listing is where value is made.</h2>
        <p className="positioning-lead">Most tools move the same listing from one marketplace to another. Listwork helps make the listing worth moving.</p>
        <div className="positioning-principles">
          <div><span>01</span><p><strong>Know what you have.</strong> Keep identity, condition and provenance together.</p></div>
          <div><span>02</span><p><strong>Price with evidence.</strong> See the comparisons behind the range.</p></div>
          <div><span>03</span><p><strong>Keep one source of truth.</strong> Refine once; adapt by channel.</p></div>
        </div>
      </div>
      <div className="positioning-image"><img src={cataloguePhoto} alt="A researched catalogue of specialist secondhand objects" /><span aria-hidden="true" /></div>
    </section>
  );
}

function EvidenceSection() {
  return (
    <section className="evidence-section" id="product">
      <div className="evidence-demo" aria-label="Illustrative Listwork research workspace">
        <div className="evidence-appbar"><span><ListworkMark /> <strong>listwork</strong></span><small>Item VNT-2041 · Research</small><i aria-hidden="true" /></div>
        <div className="evidence-body">
          <aside aria-label="Item workflow"><b>ITEM VNT-2041</b><span>Capture</span><span>Identify</span><span className="active">Research</span><span>Refine</span><span>List</span></aside>
          <div className="evidence-record">
            <p className="marketing-eyebrow">Comparable evidence</p>
            <h3>Navy chore jacket</h3>
            <p>Three selected sales support a tighter range.</p>
            <div className="evidence-table">
              <div><strong>£142</strong><span>Very good</span><span>14 days</span></div>
              <div className="selected"><strong>£156</strong><span>Good</span><span>31 days</span><i /></div>
              <div><strong>£149</strong><span>Very good</span><span>8 days</span></div>
            </div>
            <div className="evidence-range"><span>Suggested range</span><strong>£128–£156</strong><small>Based on selected evidence</small></div>
          </div>
        </div>
      </div>
      <div className="evidence-copy">
        <p className="marketing-eyebrow">Visible reasoning</p>
        <h2>See the case, not just the answer.</h2>
        <p>Listwork keeps the evidence close to the decision. You can see what shaped a range, choose what matters and edit the result.</p>
        <ul><li><strong>Evidence stays visible</strong><span>No unexplained recommendation.</span></li><li><strong>Your judgment stays in control</strong><span>Select, reject and refine the inputs.</span></li><li><strong>The record stays reusable</strong><span>One item story, shaped for each channel.</span></li></ul>
      </div>
    </section>
  );
}

function AudienceSection() {
  return (
    <section className="audience-section" id="specialists">
      <ListworkMark className="audience-mark" />
      <div className="marketing-container">
        <p className="marketing-eyebrow on-blue">Built for the trained eye</p>
        <h2>You notice what others miss.</h2>
        <p>Listwork gives specialist judgment somewhere to work. It is designed for sellers whose knowledge of maker, era, material and condition changes what an item is worth.</p>
        <div className="audience-types"><span>Vintage fashion</span><span>Designer accessories</span><span>Specialist resale</span></div>
      </div>
    </section>
  );
}

function CapabilitySection() {
  const capabilities = [
    ["Research with context", "Keep identification clues, condition notes and comparable evidence beside the item instead of across browser tabs."],
    ["Create stronger listings", "Turn one considered item record into marketplace-specific titles, descriptions, prices and guided listing workflows."],
    ["Run the whole record", "Keep photos, listings, orders, realised profit and exportable backups connected after the item goes live."]
  ];
  return (
    <section className="capability-section">
      <div className="marketing-container capability-heading"><p className="marketing-eyebrow">One item record</p><h2>Research first. Operations included.</h2><p>Cross-listing, stock control and profit remain part of Listwork. They follow the considered item record instead of replacing it.</p></div>
      <div className="marketing-container capability-grid">
        {capabilities.map(([title, copy], index) => <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article>)}
      </div>
    </section>
  );
}

function PricingSection({ compact = false }: { compact?: boolean }) {
  return (
    <section className={compact ? "pricing-section compact" : "pricing-section"} id="pricing">
      <div className="marketing-container">
        <div className="section-heading pricing-heading">
          <p className="marketing-eyebrow">Clear pricing · No sales commission</p>
          <h2>Choose capacity, not a cut of your expertise.</h2>
          <p>Every plan includes the complete inventory, listing-draft, guided-posting, order, photo and profit workflow. As your operation grows, you pay for more stock, storage and seats.</p>
        </div>
        <div className="pricing-grid">
          {plans.map((plan) => (
            <article className={plan.featured ? "pricing-card featured" : "pricing-card"} key={plan.name}>
              {plan.featured && <span className="pricing-label">Best for growing sellers</span>}
              <h3>{plan.name}</h3>
              <p>{plan.description}</p>
              <div className="plan-price"><strong>{plan.price}</strong><span>/ month<br />including VAT</span></div>
              <a className={plan.featured ? "marketing-button full" : "marketing-button secondary full"} href={createAccountURL} data-conversion={`pricing-${plan.name.toLowerCase()}-trial`}>Start 14-day trial <ArrowRight size={17} /></a>
              <ul>{plan.features.map((feature) => <li key={feature}><Check size={17} /> {feature}</li>)}</ul>
            </article>
          ))}
        </div>
        {!compact && <p className="pricing-trial-note">Every trial starts on Starter. No card required; choose another paid plan when you are ready.</p>}
        {compact && <div className="pricing-more"><span>Start with the Starter workflow for 14 days, no card required. Choose any paid plan to keep working when it ends.</span><a href="/pricing">Compare plans <ArrowRight size={16} /></a></div>}
      </div>
    </section>
  );
}

function FaqSection() {
  const questions = [
    ["Is Listwork another cross-listing tool?", "No. Cross-listing is part of the workflow, but Listwork starts earlier: with identification, evidence, condition, pricing and the seller judgment that makes a listing stronger."],
    ["Does Listwork source inventory for me?", "No. Listwork starts once an item is in your hands. It helps you understand, position and list inventory you have already found."],
    ["Does Listwork list directly on marketplaces?", "Listwork prepares channel-specific drafts for eBay, Etsy, Shopify, Vinted and Depop. Direct connections are capability-gated: eBay, Etsy and Shopify use approved API accounts; Depop requires Partner API approval; Vinted requires an allowlisted Pro integration. Standard Depop and Vinted accounts use guided handoff."],
    ["Do I need a card for the trial?", "No. Create your workspace and use the paid Starter workflow for 14 days without entering payment details."],
    ["What happens when the trial ends?", "Your workspace becomes read-only until you choose a paid plan. Your records are retained, but creating inventory, uploading photos and preparing new listing drafts requires an active subscription."],
    ["Does Listwork take a percentage of my sales?", "No. Listwork charges a fixed subscription. Marketplace, payment and postage fees remain separate and can be recorded against each sale for profit reporting."],
    ["Which marketplaces can I prepare listings for?", "Listwork prepares listings for eBay, Etsy, Shopify, Vinted and Depop. Your Settings show whether each account is using a direct API connection or a guided workflow, including any marketplace approval requirement."],
    ["Can I cancel any time?", "Yes. Paid subscriptions can be cancelled from billing settings and remain active until the end of the current billing period."]
  ];

  return (
    <section className="faq-section">
      <div className="marketing-container faq-layout">
        <div><p className="marketing-eyebrow">Straight answers</p><h2>Before you start.</h2></div>
        <div className="faq-list">
          {questions.map(([question, answer], index) => (
            <details key={question} open={index === 0}><summary>{question}<span aria-hidden="true">+</span></summary><p>{answer}</p></details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCallToAction() {
  return (
    <section className="final-cta">
      <div className="marketing-container final-cta-inner">
        <div><p className="marketing-eyebrow on-dark">Worth a closer look</p><h2>Make the next listing worthy of the item.</h2></div>
        <div><a className="marketing-button light" href={createAccountURL} data-conversion="final-start-trial">Start your 14-day trial <ArrowRight size={18} /></a><span>No card required. Plans start at £12 a month.</span></div>
      </div>
    </section>
  );
}

function MarketingFooter() {
  return (
    <footer className="marketing-footer" id="site-footer">
      <div className="marketing-container footer-main">
        <div className="footer-brand"><BrandLink /><p>Research and listing tools for specialist resellers.</p><a href="mailto:support@listwork.co">support@listwork.co</a></div>
        <div className="footer-links"><strong>Product</strong><a href="/#method">Method</a><a href="/#product">Product</a><a href="/pricing">Pricing</a><a href={signInURL}>Sign in</a></div>
        <div className="footer-links"><strong>Legal</strong><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/cookies">Cookies</a></div>
        <div className="footer-company"><strong>Company</strong><p>{legalCompanyName}<br />Registered in England and Wales<br />Company number 15250992</p></div>
      </div>
      <div className="marketing-container footer-bottom"><span>© {new Date().getFullYear()} N-Dimensional Limited</span><span>Listwork is independent of the marketplaces named on this site.</span></div>
    </footer>
  );
}

function LegalPage({ title, summary, children }: { title: string; summary: string; children: ReactNode }) {
  return (
    <div className="marketing-site legal-site">
      <MarketingHeader />
      <main id="main-content">
        <header className="legal-hero"><div className="marketing-container"><p className="marketing-eyebrow">Listwork legal</p><h1>{title}</h1><p>{summary}</p><span>Effective 28 July 2026</span></div></header>
        <div className="marketing-container legal-layout">
          <aside><strong>Contact</strong><a href="mailto:support@listwork.co">support@listwork.co</a><p>{legalCompanyName}<br />Company number 15250992</p></aside>
          <article className="legal-content">{children}</article>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}

function PrivacyContent() {
  return (
    <>
      <LegalSection title="1. Who we are"><p>{legalCompanyName} is the controller of personal information processed through Listwork. Our registered office is {legalAddress}. Questions and rights requests can be sent to <a href="mailto:support@listwork.co">support@listwork.co</a>.</p></LegalSection>
      <LegalSection title="2. Information we collect"><p>We collect account and workspace information such as your name, email address, business name, sign-in credentials, team membership and account settings. We also process the inventory, listing, order, buyer-reference, product-photo and financial records that you choose to store in Listwork.</p><p>If you enable marketplace email monitoring, we process marketplace notification emails forwarded to your unique Listwork address. We retain the detected event details, sender domain, subject, message fingerprint and any listing reference or sale amount needed for review. Raw message content and attachments are discarded after parsing.</p><p>We collect technical and security information including session identifiers, device and browser information, approximate network location, authentication events, audit records, error data and service usage needed to secure and operate the product.</p></LegalSection>
      <LegalSection title="3. How we use information"><p>We use information to provide and secure Listwork, authenticate users, synchronise workspace data, store product photos, calculate reports, deliver service messages, provide support, process subscriptions, prevent abuse and meet legal obligations.</p><p>Our main UK GDPR lawful bases are performance of our contract, our legitimate interests in operating and securing the service, compliance with legal obligations, and consent where the law specifically requires it.</p></LegalSection>
      <LegalSection title="4. Assistive AI"><p>When you use research or the workspace assistant, Listwork sends the question and the relevant inventory, listing, order or business context to the configured AI model provider so it can produce the requested answer. The configured providers may include Cloudflare and OpenAI. We also record request counts, model tier, token counts, estimated cost, provider errors and action audit events to operate the service, control plan usage and investigate problems.</p><p>AI output can be incomplete or wrong. Listwork presents proposed business changes for your review and requires confirmation before applying approval-gated actions. Avoid entering unrelated sensitive personal information in assistant prompts.</p></LegalSection>
      <LegalSection title="5. Service providers and international transfers"><p>We use specialist providers for infrastructure, object storage, AI processing, email delivery, abuse prevention and payment processing. These currently include Cloudflare and Stripe, may include OpenAI for configured AI features, and may include Resend for transactional email. Providers process information under contractual and security obligations.</p><p>Where information is transferred outside the UK, we use an applicable adequacy decision or recognised contractual safeguards.</p></LegalSection>
      <LegalSection title="6. Payments"><p>Payment details are collected and processed by Stripe through its hosted checkout and billing services. Listwork does not receive or store your complete card number. Stripe's own privacy notice also applies to information it processes for payments, fraud prevention and legal compliance.</p></LegalSection>
      <LegalSection title="7. Retention"><p>We keep account and workspace information while your account is active and for a limited period afterwards where needed for recovery, security, dispute resolution or legal compliance. Payment and transaction records may be kept for the statutory accounting period. Security logs and expired authentication records are removed or anonymised according to operational retention schedules.</p></LegalSection>
      <LegalSection title="8. Your rights"><p>Depending on the circumstances, you may ask for access, correction, deletion, restriction, portability or objection to processing. You may also complain to the UK Information Commissioner's Office. We may need to verify your identity before completing a request.</p></LegalSection>
      <LegalSection title="9. Your marketplace and buyer data"><p>You are responsible for ensuring that information you enter about buyers, marketplace transactions and other people is lawful, relevant and no more than you need. Listwork processes that workspace content on your instructions to provide the service.</p></LegalSection>
      <LegalSection title="10. Security and changes"><p>We use access controls, encrypted transport, private storage, audit records and other proportionate safeguards. No online service can guarantee absolute security. We may update this notice when the product or law changes and will publish the revised effective date here.</p></LegalSection>
    </>
  );
}

function TermsContent() {
  return (
    <>
      <LegalSection title="1. These terms"><p>These terms form an agreement between you and {legalCompanyName}, registered at {legalAddress}. By creating an account or using Listwork, you agree to them. If you use Listwork for an organisation, you confirm that you can bind that organisation.</p></LegalSection>
      <LegalSection title="2. The service"><p>Listwork provides inventory, product-photo, assistive AI, marketplace listing preparation, guided or approved direct connections, sales, fulfilment and reporting tools for resale businesses. AI output requires your judgment and may be incomplete or wrong. Approval-gated assistant actions are only applied after you confirm them. Marketplace features and availability can change as third-party platforms change their services and rules.</p><p>Listwork is independent of eBay, Vinted, Etsy, Depop, Shopify and any other marketplace named in the product. We do not guarantee marketplace approval, listing visibility, sale volume or continued access to a third-party platform.</p></LegalSection>
      <LegalSection title="3. Accounts and security"><p>You must be at least 18 and provide accurate account information. Keep your email account, passkeys, devices and sessions secure. You are responsible for activity performed through your account except where caused by our breach. Tell us promptly at support@listwork.co if you suspect unauthorised access.</p></LegalSection>
      <LegalSection title="4. Trials and subscriptions"><p>New workspaces may receive a 14-day trial without a payment card. When the trial ends, the workspace becomes read-only until you select a paid plan. We will show the plan price and billing period before you confirm payment.</p><p>Paid subscriptions renew automatically until cancelled. You can cancel through the billing controls; cancellation takes effect at the end of the paid period unless checkout states otherwise. Except where law requires or a checkout promise says otherwise, part-used periods are not refundable.</p></LegalSection>
      <LegalSection title="5. Taxes and payment processing"><p>Displayed UK launch prices include VAT where stated. Stripe processes subscription payments and may calculate, collect or remit taxes according to the checkout terms and transaction location. The hosted checkout identifies the entity responsible for the transaction before you pay.</p></LegalSection>
      <LegalSection title="6. Your content"><p>You keep ownership of the records and images you upload. You give us a limited right to host, copy, transform and transmit that content only as needed to operate, secure and improve the service. You confirm that you have the rights and lawful basis needed to use the content.</p></LegalSection>
      <LegalSection title="7. Acceptable use"><p>Do not use Listwork for unlawful goods, fraud, infringement, harassment, malware, unauthorised access, platform-rule evasion or activity that harms the service or others. Do not reverse engineer, scrape or overload the service except where applicable law cannot restrict that activity.</p></LegalSection>
      <LegalSection title="8. Availability and changes"><p>We aim to provide a reliable service but do not promise uninterrupted operation. We may maintain, improve, limit or discontinue features. Where a material change negatively affects a paid subscription, we will give reasonable notice when practicable.</p></LegalSection>
      <LegalSection title="9. Suspension and termination"><p>You may stop using Listwork at any time. We may suspend or terminate access for material breach, non-payment, security risk, unlawful activity or where necessary to protect the service. You can export workspace data using the product tools while you have access. Account deletion is permanent once confirmed.</p></LegalSection>
      <LegalSection title="10. Liability"><p>Nothing in these terms excludes liability that cannot legally be excluded, including liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation. Subject to that, we are not liable for indirect or consequential loss, loss of profit, lost marketplace opportunity, or third-party platform action.</p><p>Our total liability arising from the service in any 12-month period is limited to the greater of £100 and the fees you paid for Listwork during that period. These limits do not affect statutory rights that apply to you.</p></LegalSection>
      <LegalSection title="11. Governing law"><p>These terms are governed by the law of England and Wales. The courts of England and Wales have jurisdiction, subject to any mandatory rights you have to bring a claim elsewhere.</p></LegalSection>
      <LegalSection title="12. Contact"><p>Questions about these terms can be sent to <a href="mailto:support@listwork.co">support@listwork.co</a>.</p></LegalSection>
    </>
  );
}

function CookiesContent() {
  return (
    <>
      <LegalSection title="1. What Listwork stores"><p>Listwork currently uses browser storage and cookies that are strictly necessary to provide account sessions, security controls, workspace preferences, local product data and offline-capable behaviour. We do not currently use advertising cookies.</p></LegalSection>
      <LegalSection title="2. Essential account cookies"><p>When you sign in to the cloud service, an HTTP-only session cookie keeps you authenticated and protects access to your workspace. Security services may also store short-lived data needed to detect automated or abusive requests. Disabling these controls can prevent account features from working.</p></LegalSection>
      <LegalSection title="3. Local storage"><p>Listwork may use local browser storage for display preferences, cached application files, local demo data and photo-processing models. This information stays on the device unless a feature explicitly synchronises it to the service.</p></LegalSection>
      <LegalSection title="4. Future analytics"><p>If we introduce non-essential analytics or marketing cookies, we will update this notice and provide a consent control where required before placing them.</p></LegalSection>
      <LegalSection title="5. Managing storage"><p>You can clear cookies and local storage through your browser settings. Doing so may sign you out, remove local-only workspace data or require application resources to be downloaded again.</p></LegalSection>
      <LegalSection title="6. Contact"><p>Questions about Listwork's use of cookies and browser storage can be sent to <a href="mailto:support@listwork.co">support@listwork.co</a>.</p></LegalSection>
    </>
  );
}

function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return <section><h2>{title}</h2>{children}</section>;
}

function useDocumentMetadata(route: MarketingRoute) {
  useEffect(() => {
    const titles: Record<MarketingRoute, string> = {
      home: "Listwork | Know what you have. Show what it’s worth.",
      pricing: "Pricing | Listwork",
      privacy: "Privacy notice | Listwork",
      terms: "Terms of service | Listwork",
      cookies: "Cookie notice | Listwork"
    };
    const descriptions: Record<MarketingRoute, string> = {
      home: "Listwork helps specialist resellers identify, research and position distinctive inventory, then create stronger marketplace-ready listings.",
      pricing: "Simple Listwork plans for specialist resale businesses, with a 14-day trial and no marketplace commission.",
      privacy: "How Listwork collects, uses and protects personal information.",
      terms: "The terms that apply when you create or use a Listwork account.",
      cookies: "How Listwork uses essential cookies and browser storage."
    };
    document.title = titles[route];
    let description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.appendChild(description);
    }
    description.content = descriptions[route];

    const canonicalURL = route === "home" ? "https://listwork.co/" : `https://listwork.co/${route}`;
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalURL;

    const updateMeta = (selector: string, attribute: "property" | "name", key: string, content: string) => {
      let element = document.querySelector<HTMLMetaElement>(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.content = content;
    };
    updateMeta('meta[property="og:title"]', "property", "og:title", titles[route]);
    updateMeta('meta[property="og:description"]', "property", "og:description", descriptions[route]);
    updateMeta('meta[property="og:url"]', "property", "og:url", canonicalURL);
    updateMeta('meta[name="twitter:title"]', "name", "twitter:title", titles[route]);
    updateMeta('meta[name="twitter:description"]', "name", "twitter:description", descriptions[route]);
  }, [route]);
}
